<template>
  <v-data-table
    :headers="headers"
    :items="items"
    :search="search"
    v-bind="pagingOptions"
    item-key="uid"
    :expanded.sync="expanded"
    class="t-items-table"
    sort-by="order"
  >
    <template v-slot:item.qty="{ item }">
      <v-edit-dialog :return-value.sync="item.qty" lazy large>
        {{ item.qty }}
        <template v-slot:input>
          <v-text-field v-model.number="item.qty" label="Qty" single-line type="number" min="1"></v-text-field>
        </template>
      </v-edit-dialog>
    </template>
    <template v-slot:item.adults="{ item }">
      <v-edit-dialog :return-value.sync="item.adults" lazy large @save="recalcQty(item)">
        {{ item.adults }}
        <template v-slot:input>
          <v-text-field v-model.number="item.adults" label="Adults" single-line type="number" min="0"></v-text-field>
        </template>
      </v-edit-dialog>
    </template>
    <template v-slot:item.children="{ item }">
      <v-edit-dialog :return-value.sync="item.children" lazy large @save="recalcQty(item)">
        {{ item.children }}
        <template v-slot:input>
          <v-text-field v-model.number="item.children" label="Children" single-line type="number" min="0"></v-text-field>
        </template>
      </v-edit-dialog>
    </template>
    <template v-slot:item.hidden="{ item }">
      <v-simple-checkbox v-model="item.hidden"></v-simple-checkbox>
    </template>
    <template v-slot:item.optional="{ item }">
      <v-simple-checkbox v-model="item.optional"></v-simple-checkbox>
    </template>
    <template v-slot:item.included="{ item }">
      <v-checkbox
        v-model="item.included"
        @change="onItemIncludedChange(item)"
      ></v-checkbox>
    </template>
    <template v-slot:item.supplierName="{ item }">
      <supplier-label
        v-model="item.supplierName"
        :supplier-type="item.serviceType"
        :supplier-id="item.supplierId"
        :callback="onShowSupplier(item)"
      ></supplier-label>
    </template>
    <template v-slot:item.startDay="props">
      <v-edit-dialog
        :return-value.sync="props.item.startDay"
        @save="onSaveServiceDay(props.item)"
      >
        {{ props.item.startDay }}
        <template v-slot:input>
          <v-autocomplete
            v-model="props.item.startDay"
            :items="templateDays"
            single-line
          ></v-autocomplete>
        </template>
      </v-edit-dialog>
    </template>
    <template v-slot:item.order="{ item }">
      <v-edit-dialog :return-value.sync="item.order" lazy large>
        {{ item.order }}
        <template v-slot:input>
          <v-text-field
            v-model="item.order"
            label="Edit"
            single-line
            type="number"
          ></v-text-field>
        </template>
      </v-edit-dialog>
    </template>
    <template v-slot:item.duration="props">
      <v-edit-dialog
        :return-value.sync="props.item.duration"
        @save="onSaveServiceDuration(props.item)"
      >
        {{ props.item.duration }}
        <template v-slot:input>
          <v-autocomplete
            v-model="props.item.duration"
            :items="itemAvailableDurations(props.item)"
            single-line
          ></v-autocomplete>
        </template>
      </v-edit-dialog>
    </template>
    <template v-slot:item.ratePlan="{ item }">
      <v-edit-dialog :return-value.sync="item.ratePlan" lazy large>
        {{ item.ratePlan }}
        <template v-slot:input>
          <v-text-field
            v-model="item.ratePlan"
            label="Rate Plan"
            single-line
          ></v-text-field>
        </template>
      </v-edit-dialog>
    </template>
    <template v-slot:item.serviceName="{ item }">
      <span>
        {{ item.serviceName }}
        <v-tooltip bottom v-if="item.parentUid && item.linkParent">
          <template v-slot:activator="{ on, attrs }">
            <v-icon color="light-green" v-bind="attrs" v-on="on" class="ml-1">
              mdi-alpha-l-circle-outline
            </v-icon>
          </template>
          <span>Link Parent: {{ item.linkParent }}</span>
        </v-tooltip>
        <v-btn
          text
          small
          icon
          v-if="item.extras && item.extras.length > 0"
          @click="expandExtras(item)"
        >
          <v-icon color="yellow darken-2">mdi-star</v-icon>
        </v-btn>
      </span>
    </template>
    <template v-slot:expanded-item="{ headers, item }">
      <td :colspan="headers.length" class="pa-0">
        <table>
          <tr v-for="extra in item.extras" :key="extra.title">
            <td v-for="header in headers" :key="header.name">
              <template v-if="header.name == 'serviceType'">
                <v-icon>{{ supplierTypeIcon(item.serviceType) }}</v-icon>
              </template>
              <template v-else-if="header.name == 'serviceName'">
                <v-icon color="yellow darken-2" small>mdi-star</v-icon>
                {{ extra.name }}
              </template>
              <template v-else-if="header.name == 'startDay'">
                <v-edit-dialog
                  :return-value.sync="extra.startDay"
                  @save="onSaveExtraDay(item, extra)"
                >
                  {{ extra.startDay }}
                  <template v-slot:input>
                    <v-autocomplete
                      v-model="extra.startDay"
                      :items="extraTemplateDays(item)"
                      single-line
                    ></v-autocomplete>
                  </template>
                </v-edit-dialog>
              </template>
              <template v-else-if="header.name == 'duration'">
                <v-edit-dialog :return-value.sync="extra.duration">
                  {{ extra.duration }}
                  <template v-slot:input>
                    <v-autocomplete
                      v-model="extra.duration"
                      :items="extraAvailableDurations(item, extra)"
                      single-line
                    ></v-autocomplete>
                  </template>
                </v-edit-dialog>
              </template>
              <template v-else-if="header.name == 'actions'">
                <confirmation-dialog
                  :item="item"
                  @onConfirm="onExtraRemove(extra, item)"
                >
                  <v-btn small icon class="mx-0">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-icon small color="pink" v-on="on">mdi-delete</v-icon>
                      </template>
                      <span>Remove Extra</span>
                    </v-tooltip>
                  </v-btn>
                  <p slot="message">This will remove service.</p>
                </confirmation-dialog>
              </template>
              <template v-else-if="header.name == 'optional'">
                <v-checkbox
                  v-model="extra.optional"
                  :disabled="!item.included"
                ></v-checkbox>
              </template>
              <template v-else-if="header.name == 'included'">
                <v-checkbox
                  v-model="extra.included"
                  :disabled="!item.included"
                ></v-checkbox>
              </template>
              <template v-else-if="header.name == 'hidden'">
                <v-checkbox
                  v-model="extra.hidden"
                  :disabled="!item.included"
                ></v-checkbox>
              </template>
              <template v-else>{{ item[header.value] }}</template>
            </td>
          </tr>
        </table>
      </td>
    </template>
    <template v-slot:item.serviceType="{ item }">
      <v-icon>{{ supplierTypeIcon(item.serviceType) }}</v-icon>
    </template>
    <template v-slot:item.actions="{ item }">
      <div class="d-flex align-center">
        <v-btn small icon class="mx-0" @click="onItemRemarks(item)">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-icon small color="primary" v-on="on"
                >mdi-note-outline</v-icon
              >
            </template>
            <span>Remarks</span>
          </v-tooltip>
        </v-btn>
        <confirmation-dialog :item="item" @onConfirm="onItemRemove">
          <v-btn small icon class="mx-0">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon small color="pink" v-on="on">mdi-delete</v-icon>
              </template>
              <span>Remove Item</span>
            </v-tooltip>
          </v-btn>
          <p slot="message">This will remove service.</p>
        </confirmation-dialog>
      </div>
    </template>
  </v-data-table>
</template>

<script>
import { SupplierTypeIconMixin } from "../../../../mixins/SupplierTypeIconMixin";
import ConfirmationDialog from "../../shared/ConfirmationDialog";
import SupplierLabel from "../../shared/labels/SupplierLabel.vue";
import { pagingOptions } from "../../../../helpers/index.js";
export default {
  name: "open-group-items-table",

  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    search: {
      type: String,
      default: () => "",
    },
    hideFooter: {
      type: Boolean,
      default: () => false,
    },
    templateDays: {
      type: Array,
      default: () => [],
    },
    duration: {
      type: Number,
      default: () => 7,
    },
  },

  mixins: [SupplierTypeIconMixin],

  components: { ConfirmationDialog, SupplierLabel },

  data() {
    return {
      pagingOptions,
      expanded: [],
    };
  },

  methods: {
    recalcQty(item) {
      if (!item.stdOccupancy) return;
      item.qty = Math.ceil((Number(item.adults) + Number(item.children)) / Number(item.stdOccupancy)) || 1;
    },
    onItemIncludedChange(item) {
      this.$emit("itemIncludedChange", item);
    },
    onItemRemove(item) {
      this.$emit("onItemRemove", item);
    },
    onItemRemarks(item) {
      this.$emit("onItemRemarks", item);
    },
    onSaveServiceDuration(item) {
      this.$emit("onSaveServiceDuration", item);
    },
    onSaveServiceDay(item) {
      this.$emit("onSaveServiceDay", item);
    },
    onExtraRemove(item) {
      this.$emit("onExtraRemove", item);
    },
    expandExtras(item) {
      const isExpanded = this.expanded.find((i) => i.id == item.id);
      this.expanded = isExpanded
        ? [...this.expanded.filter((i) => i.id != item.id)]
        : [...this.expanded, item];
    },
    extraTemplateDays(item) {
      const days = [];
      for (
        let i = parseInt(item.startDay);
        i <= Number(item.startDay) + Number(item.duration) - 1;
        i++
      ) {
        days.push(i);
      }
      return days;
    },
    extraAvailableDurations(item, extra) {
      let maxDur =
        Number(item.startDay) + Number(item.duration) - Number(extra.startDay);
      if (maxDur <= 0) {
        maxDur = 1;
      }
      return [...Array(maxDur).keys()].map((i) => ++i);
    },
    itemAvailableDurations(item) {
      return [...Array(this.itemMaxDuration(item)).keys()].map((i) => ++i);
    },
    itemMaxDuration(item) {
      let maxDuration = this.duration - Number(item.startDay);
      if (maxDuration <= 0) {
        maxDuration = 1;
      }
      return maxDuration;
    },
    onShowSupplier(payload) {
      return () => {
        this.$emit("supplier-edit", payload);
      };
    },
  },
};
</script>

<style>
.t-items-table tr td:first-child:not(:only-child) {
  width: 80px;
}
.t-items-table tr td:last-child:not(:only-child) {
  width: 100px;
}
.t-items-table tr td:nth-child(2),
.t-items-table tr td:nth-child(3),
.t-items-table tr td:nth-child(4) {
  width: 100px;
}
.t-items-table tr td:nth-child(5),
.t-items-table tr td:nth-child(6) {
  width: 300px;
}
.t-items-table tr td:nth-child(7),
.t-items-table tr td:nth-child(8),
.t-items-table tr td:nth-child(9) {
  width: 100px;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/open-groups/builder/OpenGroupItemsTable.vue